import React from 'react';
import PropTypes from 'prop-types';
import { QueryVal } from 'Helpers/Query';
import useAffordance from 'Hooks/useAffordance';
import { selectResource } from './Actions';

const SelectionStateContext = React.createContext(undefined);
const SelectionDispatchContext = React.createContext(undefined);

function getSelfLinkInternal(resource) {
  return resource.link('self').url();
}

function init(initialState) {
  const queryValName = initialState.queryValName || 'selectedResourceSelfLink';
  const getSelfLink = initialState.getSelfLink || getSelfLinkInternal;
  const metaOverride = initialState.metaOverride || null;

  return {
    queryValName,
    getSelfLink,
    metaOverride,
    selectedResourceSelfLink: QueryVal(queryValName),
    selectedResource: null,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case 'selectResource': {
      const { getSelfLink } = state;
      return {
        ...state,
        selectedResourceSelfLink: getSelfLink(action.selectedResource),
        selectedResource: action.selectedResource,
      };
    }
    case 'unselectResource': {
      return {
        ...state,
        selectedResourceSelfLink: null,
        selectedResource: null,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function SelectionProvider({
  children,
  initialState,
  onFetch,
}) {
  const [state, dispatch] = React.useReducer(reducer, initialState, init);
  const { queryValName } = state;

  let link = QueryVal(queryValName);
  // If this link has been URI encoded (even multiple times over), decode it until it's clearly not URI encoded
  while (typeof link === 'string' && (link.startsWith('http%') || link.startsWith('https%'))) {
    link = decodeURIComponent(link);
  }

  useAffordance(link, {
    onlyRunOnMount: true,
    onFetch: (resp) => {
      const [resource] = resp;
      selectResource(dispatch, state, resource, resp);
      if (onFetch) {
        onFetch(resource);
      }
    },
  });

  return (
    <SelectionStateContext.Provider value={state}>
      <SelectionDispatchContext.Provider value={dispatch}>
        {children}
      </SelectionDispatchContext.Provider>
    </SelectionStateContext.Provider>
  );
}

function useSelectionContextState() {
  const context = React.useContext(SelectionStateContext);

  if (context === undefined) {
    throw new Error('useSelectionContextState must be used within a SelectionProvider');
  }

  return context;
}

function useSelectionContextDispatch() {
  const context = React.useContext(SelectionDispatchContext);

  if (context === undefined) {
    throw new Error('useSelectionContextDispatch must be used within a SelectionProvider');
  }

  return context;
}

const useSelectionContext = () => {
  const dispatch = React.useContext(SelectionDispatchContext);
  const state = React.useContext(SelectionStateContext);

  if (!dispatch) {
    throw new Error('useSelectionContext must be called from DispatchContext.Provider');
  }

  if (!state) {
    throw new Error('useSelectionContext must be called from StateContext.Provider');
  }

  return { dispatch, state };
};

SelectionProvider.defaultProps = {
  onFetch: null,
};

SelectionProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  initialState: PropTypes.object.isRequired,
  onFetch: PropTypes.func,
};

export {
  SelectionProvider,
  useSelectionContext,
  useSelectionContextState,
  useSelectionContextDispatch,
};
